import { useTranslation } from 'react-i18next';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import { Chip } from '../../components/Chip';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { MenuItem } from './core/menu-item';
import { translateMenuTitle } from './core/menu-i18n';

export interface SidebarMenuItemProps {
  item: MenuItem;
  groupKey: string;
  depth: number;
  expanded: boolean;
  active: boolean;
  collapsed: boolean;
  onToggle: () => void;
  onNavigate: (item: MenuItem) => void;
}

/**
 * Um item de menu não decide nada: expande ou navega conforme já vem
 * calculado (`item.submenu.length > 0`), e delega a decisão de destaque/
 * expansão ao chamador via `active`/`expanded`.
 */
export function SidebarMenuItem({
  item,
  depth,
  expanded,
  active,
  collapsed,
  onToggle,
  onNavigate,
}: SidebarMenuItemProps): JSX.Element {
  const { t } = useTranslation();
  const label = translateMenuTitle(item.title, t);
  const hasSubmenu = item.submenu.length > 0;
  // Só o rótulo textual do nível superior se esconde quando colapsada; níveis
  // mais profundos só ficam visíveis com a sidebar já expandida (via Collapse).
  const hideLabel = collapsed && depth === 0;

  function handleClick(): void {
    if (hasSubmenu) {
      onToggle();
    } else {
      onNavigate(item);
    }
  }

  return (
    <ListItemButton
      selected={active}
      onClick={handleClick}
      sx={{
        pl: 2 + depth * 2,
        pr: 1,
        py: '8px',
        my: 0.5,
        color: 'inherit',
        // Tokens dedicados: os tons de hover/selected que o MUI deriva de `primary`
        // pressupõem fundo claro e somem sobre a sidebar, cujo fundo já é a própria
        // primária.
        '&:hover': { bgcolor: 'sidebar.hover' },
        '&.Mui-selected': {
          bgcolor: 'sidebar.selected',
          '&:hover': { bgcolor: 'sidebar.selectedHover' },
        },
      }}
      aria-expanded={hasSubmenu ? expanded : undefined}
    >
      {item.icon && (
        <ListItemIcon
          sx={{
            minWidth: 24,
            position: 'relative',
            color: 'inherit',
            fontSize: 22,
            marginRight: '8px',
          }}
        >
          <Box component="i" className={item.icon} sx={{ fontSize: 'inherit' }} />
          {item.pointAttention && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'error.main',
              }}
            />
          )}
        </ListItemIcon>
      )}
      {!hideLabel && (
        <ListItemText
          primary={label}
          sx={{ display: 'block' }}
          primaryTypographyProps={{
            fontSize: 14,
            fontWeight: 600,
            fontFamily: 'Montserrat',
            letterSpacing: 0.6,
          }}
        />
      )}
      {!hideLabel && item.newFeature && (
        <Chip
          label={t('sidebar.novo')}
          size="small"
          sx={{ ml: 1, bgcolor: 'primary.contrastText', color: 'primary.main' }}
        />
      )}
      {!hideLabel && hasSubmenu && (
        <ChevronRightIcon
          fontSize="large"
          sx={{
            ml: 'auto',
            color: 'inherit',
            transition: 'transform 0.2s',
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        />
      )}
    </ListItemButton>
  );
}
